/**
 * useReportFocus — click-to-focus for the Reports sidebar breakdown cards.
 *
 * Clicking a row in Units by Model focuses the report (station metrics,
 * charts, totals, export) on that model; clicking a row in Unit Status focuses
 * it on the units currently in that status (T-069). Both narrow what the
 * report QUERIES without touching the sidebar's filters, so every row stays
 * listed and clickable, and the two combine (e.g. 2RE + Offline).
 */
import { useCallback, useMemo, useState } from 'react';

export type UnitStatusFocus = 'online' | 'offline' | 'idle';

/** Unit ids behind each Unit Status count (Idle/Charging includes unknown). */
export type StatusUnitIds = Record<UnitStatusFocus, string[]>;

/**
 * Most unit ids a status focus may send. They ride in a GET query string; the
 * server enforces the same bound (api/lib/unitIdList.js MAX_UNIT_IDS).
 */
export const MAX_FOCUS_UNIT_IDS = 300;

export function isStatusFocusable(ids: string[] | undefined): boolean {
  return !!ids && ids.length > 0 && ids.length <= MAX_FOCUS_UNIT_IDS;
}

export function useReportFocus(selectedModelCodes: string[], statusUnitIds?: StatusUnitIds) {
  const [model, setModel] = useState<string | null>(null);
  const [status, setStatus] = useState<UnitStatusFocus | null>(null);

  // A focus the sidebar filter no longer includes is dropped rather than
  // querying a model the user has deselected (which would read as empty).
  // Clear the STORED focus too (adjust-state-during-render), so reselecting
  // that model later doesn't silently re-narrow the report.
  const focusIsSelected = model !== null && selectedModelCodes.includes(model);
  if (model !== null && !focusIsSelected) {
    setModel(null);
  }
  const focusedModel = focusIsSelected ? model : null;

  // Same for status: a status whose unit list emptied (or outgrew the cap)
  // is dropped and forgotten.
  const statusIsFocusable = status !== null && isStatusFocusable(statusUnitIds?.[status]);
  if (status !== null && !statusIsFocusable) {
    setStatus(null);
  }
  const focusedStatus = statusIsFocusable ? status : null;

  const reportModelCodes = useMemo(
    () => (focusedModel ? [focusedModel] : selectedModelCodes),
    [focusedModel, selectedModelCodes],
  );

  const reportUnitIds = focusedStatus && statusUnitIds ? statusUnitIds[focusedStatus] : null;

  const toggleModel = useCallback((code: string) => {
    setModel(prev => (prev === code ? null : code));
  }, []);

  const clearFocus = useCallback(() => setModel(null), []);

  const toggleStatus = useCallback((next: UnitStatusFocus) => {
    if (!isStatusFocusable(statusUnitIds?.[next])) return;
    setStatus(prev => (prev === next ? null : next));
  }, [statusUnitIds]);

  const clearStatus = useCallback(() => setStatus(null), []);

  /** Apply the focus to another model list (e.g. saved subscription criteria). */
  const focusModelCodes = useCallback(
    (models: string[]) => (focusedModel ? [focusedModel] : models),
    [focusedModel],
  );

  return {
    focusedModel,
    reportModelCodes,
    toggleModel,
    clearFocus,
    focusModelCodes,
    focusedStatus,
    reportUnitIds,
    toggleStatus,
    clearStatus,
  };
}
